import { useRightPane } from "@homebound/beam";
import { PropsWithChildren, useLayoutEffect, useMemo, useRef, useState } from "react";
import { TakeoffLinePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffLinePanel";
import { TakeoffsBulkEditPanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsBulkEditPanel";
import {
  TakeoffsSidePanelContext,
  TakeoffsSidePanelContextValue,
  TakeoffsSidePanelMode,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import { LineType } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { useTakeoffsManagerContext } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";

/** Which line the side panel shows. Owned here since a save to a new draft rebuilds the takeoffs store. */
export function TakeoffsSidePanelProvider(props: PropsWithChildren) {
  const { children } = props;
  const { openRightPane, isRightPaneOpen } = useRightPane();
  // Reported by the mounted panel rather than set on open, since Beam's discard prompt can cancel an open
  const [shownTliId, setShownTliId] = useState<string>();
  const [addedLine, setAddedLine] = useState<TakeoffsSidePanelContextValue["addedLine"]>();
  // Beam also closes the pane itself when its host unmounts, e.g. leaving the tab
  const activeTliId = isRightPaneOpen ? shownTliId : undefined;
  // Copied into the store, like the layout state, and re-set when a save to a new draft rebuilds it
  const takeoffsStore = useTakeoffsManagerContext();
  useLayoutEffect(() => takeoffsStore.setState({ activeTliId }), [takeoffsStore, activeTliId]);
  // How the shown line's panel closes once that line is deleted
  const shownLineRef = useRef<{ tliId: string; onDeleted: VoidFunction }>();
  const openCountRef = useRef(0);

  const actions = useMemo(() => {
    function show(mode: TakeoffsSidePanelMode) {
      setAddedLine(undefined);
      // Beam only asks before swapping in a new key. Keying an edit by its line lets a re-click of the open row keep
      // its edits, while every other open gets a new key, so even re-opening the same Add form starts fresh
      const key = mode.kind === "edit" ? mode.tliId : ++openCountRef.current;
      openRightPane({
        content:
          mode.kind === "bulkEdit" ? <TakeoffsBulkEditPanel key={key} /> : <TakeoffLinePanel key={key} mode={mode} />,
      });
    }
    return {
      // `versionId` is only passed for adds, so the table can scroll to the line once the new rows load
      openEdit: (tliId: string, versionId?: string) => {
        show({ kind: "edit", tliId });
        if (versionId) setAddedLine({ tliId, versionId });
      },
      openAdd: (type: LineType) => show({ kind: "add", type }),
      openDuplicate: (tliId: string) => show({ kind: "duplicate", tliId }),
      openBulkEdit: () => show({ kind: "bulkEdit" }),
      resetAddedLine: () => setAddedLine(undefined),
      lineDeleted: (tliId: string) => {
        if (shownLineRef.current?.tliId === tliId) shownLineRef.current.onDeleted();
      },
      registerShownLine: (tliId: string, onDeleted: VoidFunction) => {
        const shownLine = { tliId, onDeleted };
        shownLineRef.current = shownLine;
        setShownTliId(tliId);
        return () => {
          if (shownLineRef.current !== shownLine) return;
          shownLineRef.current = undefined;
          setShownTliId(undefined);
        };
      },
    } satisfies Omit<TakeoffsSidePanelContextValue, "addedLine">;
  }, [openRightPane]);

  const value: TakeoffsSidePanelContextValue = useMemo(() => ({ ...actions, addedLine }), [actions, addedLine]);
  return <TakeoffsSidePanelContext.Provider value={value}>{children}</TakeoffsSidePanelContext.Provider>;
}
